import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FaCalendarTimes, FaLink, FaTimesCircle, FaTrophy } from "react-icons/fa";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MatchResultScoreboard } from "@/components/v2/home/HomePostMatchCard";
import type { CompletedMatchContext } from "@/lib/api/rsvp";
import type { PublicCycleLinkStatus } from "@/lib/v2/publicCycleLinkStatus";
import { trackPublicLinkTerminalView } from "@/lib/analytics/publicLink";
import { cn } from "@/lib/utils";

interface PublicCycleStatusScreenProps {
  status: Exclude<PublicCycleLinkStatus, "active">;
  groupName?: string;
  lastCompleted?: CompletedMatchContext | null;
  className?: string;
}

export function PublicCycleStatusScreen({
  status,
  groupName,
  lastCompleted,
  className,
}: PublicCycleStatusScreenProps) {
  const { t } = useTranslation();

  useEffect(() => {
    trackPublicLinkTerminalView(status);
  }, [status]);

  const copy = {
    stale_link: {
      icon: FaLink,
      title: t("v2.home.publicLinkStaleTitle"),
      description: t("v2.home.publicLinkStaleDesc"),
    },
    cycle_closed: {
      icon: FaCalendarTimes,
      title: t("v2.home.publicLinkClosedTitle"),
      description: t("v2.home.publicLinkClosedDesc"),
    },
    poll_cancelled: {
      icon: FaTimesCircle,
      title: t("v2.home.publicLinkPollCancelledTitle"),
      description: t("v2.home.publicLinkPollCancelledDesc"),
    },
    match_cancelled: {
      icon: FaTimesCircle,
      title: t("v2.home.publicLinkMatchCancelledTitle"),
      description: t("v2.home.publicLinkMatchCancelledDesc"),
    },
  }[status];

  const Icon = copy.icon;
  const match = lastCompleted?.match ?? null;
  const showRecap =
    status === "cycle_closed" &&
    match != null &&
    (match.winner != null || (match.scorea != null && match.scoreb != null));

  const winnerLabel =
    match?.winner === "A"
      ? t("v2.home.wonTeamA")
      : match?.winner === "B"
        ? t("v2.home.wonTeamB")
        : match?.winner === "Draw"
          ? t("v2.home.matchDraw")
          : null;

  return (
    <Card className={cn("w-full max-w-lg mx-auto", className)}>
      <CardHeader className="text-center space-y-3">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-muted">
          <Icon className="h-6 w-6 text-muted-foreground" />
        </div>
        <div className="space-y-1">
          {groupName ? (
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {groupName}
            </p>
          ) : null}
          <CardTitle>{copy.title}</CardTitle>
          <CardDescription>{copy.description}</CardDescription>
        </div>
      </CardHeader>
      {showRecap ? (
        <CardContent className="space-y-3 border-t pt-4">
          <p className="text-center text-sm font-semibold text-muted-foreground">
            {t("v2.home.publicLinkLastResult")}
          </p>
          <MatchResultScoreboard
            mode="display"
            light={match.scorea}
            dark={match.scoreb}
            teamALabel={t("v2.home.teamA")}
            teamBLabel={t("v2.home.teamB")}
          />
          {winnerLabel ? (
            <p className="flex items-center justify-center gap-1.5 text-sm font-semibold text-soccer-primary">
              <FaTrophy className="h-3.5 w-3.5" /> {winnerLabel}
            </p>
          ) : null}
        </CardContent>
      ) : null}
    </Card>
  );
}
